import { EmptyState } from '@bitrise/bitkit';
import { useDebounceValue } from 'usehooks-ts';
import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';
import { SettingsPageAbilities } from '@/pages/App/AppSettings/types/SettingsPageAbilities';
import ManualApprovalBuildList from '@/pages/App/AppDetails/components/BuildsSection/ManualApprovalBuildList/ManualApprovalBuildList';
import ArchivedBuildsSection from '@/pages/BuildList/ArchivedBuildsSection';
import ScheduledBuilds from '@/pages/App/AppDetails/components/ScheduledBuilds/ScheduledBuilds';
import { ScheduledBuild } from '@/pages/App/AppDetails/components/ScheduledBuilds/scheduledBuilds.types';
import AppForAppPage from '@/pages/App/types/AppForAppPage';
import BuildList from './BuildList/BuildList';
import useBuilds from './BuildList/useBuilds';

type BuildSectionProps = {
  app: AppForAppPage;
  abilities: SettingsPageAbilities;
  filters: BuildListFilters;
  isViewedAsPublicPage: boolean;
  onUpdateScheduledBuild: (scheduledBuild: ScheduledBuild) => void;
};

const BuildsSection = ({
  app,
  abilities,
  filters,
  isViewedAsPublicPage,
  onUpdateScheduledBuild,
}: BuildSectionProps) => {
  const [debouncedFilters] = useDebounceValue(filters, 50);

  const {
    builds,
    count,
    hasNextPage,
    fetchNextPage,
    refetch,
    isFetchingNextPage,
    isFetchingNextPageError,
    isFetching,
    isError,
    isSuccess,
  } = useBuilds(
    {
      appSlug: app.slug,
      filters: debouncedFilters,
      isOnHold: false,
      limit: 25,
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: false,
      refetchInterval: 15000,
      requestConfig: {
        publicAccess: isViewedAsPublicPage,
      },
    },
  );

  return (
    <>
      {!isViewedAsPublicPage && app.isManualApprovalEnabled && (
        <ManualApprovalBuildList app={app} abilities={abilities} />
      )}
      {!isViewedAsPublicPage && <ScheduledBuilds app={app} onUpdateScheduledBuild={onUpdateScheduledBuild} />}
      {!app.hasAnyBuild && (
        <EmptyState
          backgroundColor="transparent"
          description="Set up your Workflows or use the default ones to start running builds."
          iconName="Build"
          title="Your builds will appear here"
        />
      )}
      {app.hasAnyBuild && (
        <>
          <BuildList
            filters={filters}
            app={app}
            canRunBuild={abilities.canRunBuilds}
            builds={builds}
            count={count}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            refetch={refetch}
            isFetchingNextPage={isFetchingNextPage}
            isFetchingNextPageError={isFetchingNextPageError}
            isFetching={isFetching}
            isError={isError}
            isSuccess={isSuccess}
          />
          <ArchivedBuildsSection appSlug={app.slug} />
        </>
      )}
    </>
  );
};

export default BuildsSection;
